/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { isEmpty, isPresent } from '@ember/utils';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import EmArray from '@ember/array';
import EnvironmentService from 'cio/services/environment';
import { stringToHash } from 'cio/utils/string';
import { CREATED_IN_CIO_AT, RESTRICTED_ATTRIBUTES } from 'cio/utils/attributes';
import { IdentifierKey, ObjectIdentifierKey, ObjectTypeIdentifierKey } from 'cio/utils/identifiers';
import DS from 'ember-data';
import ImportModel from 'cio/models/import';

interface ImportCsvAttributeFieldArgs {
  attribute: Record<string, any>;
  attributes: EmArray<string>;
  mappedAttributes: string[];
  restrictedAttributes: string[];
  requiredAttribute: string;
  disableMapAttribute: boolean;
  onSkip: (attribute: Record<string, any>) => unknown;
  onMap: (attribute: Record<string, any>) => unknown;
  import: ImportModel;
}

export default class ImportCsvAttributeField extends Component<ImportCsvAttributeFieldArgs> {
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  get attributeOptions() {
    let { mappedAttributes } = this.args;
    let { isRelationshipsImport } = this.args.import;

    let attributeOptions = [
      // Remove ID since it's not used in email workspaces and will be added otherwise by the
      // required attribute addition below.
      ...this.environment.current.sentAttributes.filter(
        (attr) => ![IdentifierKey.CioId, CREATED_IN_CIO_AT].includes(attr),
      ),
      ...RESTRICTED_ATTRIBUTES.filter((attr) => attr !== IdentifierKey.Id && attr !== CREATED_IN_CIO_AT),
    ];

    if (this.args.import.isObjectsImport) {
      attributeOptions =
        this.args.import.objectType?.sentAttributes.filter(
          (attr) => !([ObjectIdentifierKey.ObjectId, ObjectIdentifierKey.CioObjectId] as string[]).includes(attr),
        ) ?? [];
    }

    // Use a Set to deduplicate values
    let allAttributes = new Set<string>([this.args.requiredAttribute, ...attributeOptions, this.args.attribute.to]);

    if (isRelationshipsImport) {
      // for relationship imports we only need to map relationship attributes
      allAttributes = new Set<string>([
        ...(this.args.import.objectType?.sentRelAttributes ?? []),
        this.args.attribute.to,
      ]);
    }

    return Array.from(allAttributes)
      .map((attribute) => {
        if (isEmpty(attribute)) {
          return;
        }

        if (
          isPresent(mappedAttributes) &&
          mappedAttributes.includes(attribute) &&
          this.args.attribute.to !== attribute &&
          this.args.attribute.mapping !== attribute
        ) {
          return;
        }

        let required = attribute === this.args.requiredAttribute;

        if (isRelationshipsImport && !required) {
          required = attribute === ObjectTypeIdentifierKey.ObjectTypeId || attribute === ObjectIdentifierKey.ObjectId;
        }

        return {
          label: attribute,
          value: attribute,
          restricted: this.args.restrictedAttributes.includes(attribute),
          required,
        };
      })
      .filter(Boolean);
  }

  get attributeMappedTwice() {
    return this.args.mappedAttributes.filter((attr) => attr === this.args.attribute.to).length > 1;
  }

  get warnings() {
    return [
      ...this.args.attribute.warnings,
      this.attributeMappedTwice ? 'You can only map one column to each attribute' : undefined,
    ].filter(Boolean);
  }

  get id() {
    return stringToHash(`enable-${this.args.attribute.key}-${this.args.attribute.toString()}`);
  }

  @action
  toggleSkip() {
    this.args.attribute.toggleProperty('skip');

    if (this.args.attribute.restricted && this.args.attribute.skip) {
      this.args.onSkip(this.args.attribute);
    } else if (this.args.attribute.restricted) {
      this.args.onMap(this.args.attribute);
    }
  }

  @action
  setAttributeTo(attr: { value: string } | null) {
    this.args.attribute.set('to', attr ? attr.value : null);
  }
}
